import { connect } from 'react-redux';
import { useEffect } from 'react';

import { AppState } from '../../stores/redux-store';
import { ApplicationEvent } from '../../models/analytics';
import { Credentials, CredentialsEntity } from '../../models/credentials';
import { applicationParameters } from '../../utilities/application-parameters';
import {
  ConfigurationLoadingState,
  useConfiguration,
} from '../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { hasHiddenTermsAndScopeMessages } from '../../models/configuration';
import { useUrlQuery } from '../../components/ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { useAnalytics } from './AnalyticsProvider';
import { ParentApplicationEvent, parentMessenger } from './ParentApplicationEventMessenger';

const getInitializedEventType = (): ApplicationEvent => {
  const hasInputProvider = applicationParameters.get('providerName');
  const hasConsentId = applicationParameters.get('consentId');

  if (hasConsentId) {
    return ApplicationEvent.INITIALIZED_WITH_CONSENT;
  }

  if (hasInputProvider) {
    return ApplicationEvent.INITIALIZED_WITH_PROVIDER;
  }

  return ApplicationEvent.INITIALIZED_WITHOUT_PROVIDER;
};

export const isPP = () => {
  const clientId = applicationParameters.get('clientId');
  const payPalClientIds = [
    '6fe594de4215409d92526d5e303e055d', // prod
    'dda00c97fc2344bd900fa4c28e769a63', // sandbox
    '7bbe03a7b4de4c07ad91afae8fca5b8d', // testing
  ];

  return clientId && payPalClientIds.includes(clientId);
};

export type ApplicationEventsProps = {
  credentialsError?: string;
  credentials?: CredentialsEntity;
  isApplicationInitialized: boolean;
};

export const ApplicationEventsCollectorPresenter: React.FC<ApplicationEventsProps> = ({
  credentials,
  credentialsError,
  isApplicationInitialized,
}) => {
  const { authorizationToken } = useUrlQuery();
  const isRedirected = Boolean(authorizationToken);
  const { tinklinkAnalytics } = useAnalytics();
  const { data: configurationData, loadingState: configurationLoadingState } = useConfiguration();

  useEffect(() => {
    if (!isRedirected && isApplicationInitialized) {
      parentMessenger.fireApplicationEvent(ParentApplicationEvent.INITIALIZED);

      const initializedApplicationEvent = getInitializedEventType();
      tinklinkAnalytics.sendApplicationEvent(initializedApplicationEvent);
    }
  }, [isApplicationInitialized, isRedirected, tinklinkAnalytics]);

  useEffect(() => {
    const isConfigurationLoaded =
      configurationLoadingState === ConfigurationLoadingState.LOADED &&
      Boolean(configurationData.featureFlags);

    if (isConfigurationLoaded) {
      const isOurLicense = !hasHiddenTermsAndScopeMessages(configurationData);

      if (isOurLicense && isApplicationInitialized && tinklinkAnalytics.isInitialized()) {
        tinklinkAnalytics.sendApplicationEventOnce(ApplicationEvent.IS_OUR_LICENSE);
      }
    }
  }, [tinklinkAnalytics, configurationData, configurationLoadingState, isApplicationInitialized]);

  useEffect(() => {
    if (Credentials.isUpdating(credentials) || Credentials.isUpdated(credentials)) {
      tinklinkAnalytics.sendApplicationEventOnce(ApplicationEvent.AUTHENTICATION_SUCCESSFUL);
      parentMessenger.fireApplicationEventOnce(ParentApplicationEvent.AUTHENTICATION_SUCCESSFUL);
    }
  }, [credentials, tinklinkAnalytics]);

  useEffect(() => {
    if (credentials?.id && isPP()) {
      parentMessenger.fireCredentialsCreatedEvent({ credentialsId: credentials.id });
    }
  }, [credentials?.id]);

  useEffect(() => {
    if (credentialsError) {
      parentMessenger.fireApplicationEvent(ParentApplicationEvent.CREDENTIALS_VALIDATION_FAILED);
      tinklinkAnalytics.sendApplicationEvent(ApplicationEvent.CREDENTIALS_VALIDATION_FAILED);
    }
  }, [credentialsError, tinklinkAnalytics]);

  return null;
};

const mapStateToProps = (state: AppState) => {
  const { user: userStore, providers: providersStore, oauth: oauthStore } = state;
  const isUserLoaded = Boolean(!userStore.loading && userStore.user && userStore.accessToken);
  const isProviderLoaded = Boolean(!providersStore.loading) && providersStore.data !== null;
  const isOauthLoaded = Boolean(oauthStore.description);

  const isApplicationInitialized = isUserLoaded && isProviderLoaded && isOauthLoaded;

  return {
    isApplicationInitialized,
    credentials: state.credentials.data,
    credentialsError: state.credentials.error,
  };
};

export const ApplicationEventsCollector = connect(mapStateToProps)(
  ApplicationEventsCollectorPresenter
);
